// Rainwater tank-sizing calculator. All math runs client-side for instant
// feedback (Danish rainfall assumption, tank lookup table, pump sizing,
// savings) — nothing here talks to Shopify. On submit it fills the native
// {% form 'contact' %} in this section with a readable summary and then lets the
// browser post it.
//
// The submit is deliberately NOT intercepted. Three rounds of doing it in the
// background all ended on Shopify's hCaptcha challenge page: a challenge cannot
// be answered inside a fetch, and it fires even with hCaptcha switched off in
// Admin whenever a submission looks programmatic. The visitor was told the
// enquiry had been sent when it had not.
//
// So the page reloads — and the figures survive it. The inputs are written to
// localStorage immediately before the submit and recalculated on the
// confirmation page, where the results render unveiled with the PDF button live.
// That is what makes the reload harmless: the visitor lands on their own result.
//
// Two percentages exist here and they mean different things — mixing them up
// makes the headline read "100 %" for every visitor:
//   rainShare = the share of the household's total water that rainwater can
//               replace. This is the headline figure the design shows.
//   coverage  = how much of that rainwater demand the roof can actually
//               deliver. Usually 100 % on a normal Danish roof, so it belongs
//               in the detail rows, not the headline.

// The roof-type dropdown was removed at the client's request. It used to carry a
// per-material runoff coefficient, but every material a visitor could actually
// pick shared 0.9 (fibercement 0.85, "andet" 0.8), so for almost everyone the
// choice changed nothing while adding eleven options to read. The three
// unusable materials — grass, thatch, copper — took the yield to zero and
// raised a warning; that path is gone with the field.
const RUNOFF_COEFF = 0.9;

const RAINFALL_MM_PER_YEAR = 700;
const FILTER_LOSS_FACTOR = 0.9; // 10% filter loss
const TOILET_LITERS_PER_PERSON_DAY = 24; // 2x6L + 4x3L
const TOTAL_LITERS_PER_PERSON_DAY = 92; // 106L minus ~13% for laundry
const MACHINE_LITERS_PER_WASH = 44;
const GARDEN_LITERS_PER_DAY = 25;

// There is no CO2 figure any more. `CO2_KG_PER_M3 = 0.044` used to drive a
// "CO2-besparelse pr. år" card in the results panel, but neither the emission
// factor nor what it was a factor OF was documented anywhere — the page stated
// a climate figure it could not account for. It is removed from the panel, the
// PDF and the enquiry e-mail alike rather than left computed but hidden: an
// undocumented number the merchant can still read and quote is the same claim
// one step removed. Restoring it needs a sourced factor and a stated method,
// not just the constant back.

// The three assumptions below are quoted to the visitor verbatim. They are read
// by `disclaimerText()` and `assumptionsText()`, which rewrite the disclaimer
// and the conversion note in the DOM on connect — so changing a figure here
// changes the sentence on the page, the one in the PDF and the one at the foot
// of the enquiry e-mail together. The literal numbers also appear in
// `calculator.form.disclaimer` / `calculator.results.conversion_note`; those are
// only the fallback for a visitor whose JavaScript never runs, but they should
// be kept in step by hand.
const DKK_PER_M3 = 70;
const SHOWER_LITERS = 44;
const COFFEE_LITERS = 0.2;

// Below this width a bar segment has no room for its own label.
const SEGMENT_LABEL_MIN_PCT = 12;

// Where the inputs are parked. Namespaced, because storage is shared with
// everything else on the domain.
const STORAGE_KEY = 'brugregnvand:beregner';

// Seven days, the window the client asked for. localStorage rather than a
// cookie, deliberately: a cookie is sent to Shopify in a request header on
// EVERY page load, so the visitor's name, phone, e-mail and address would
// travel over the wire on every click for a week. localStorage never leaves the
// browser, and the expiry is kept by hand in the stored payload, which gives
// exactly the same behaviour with none of that exposure.
const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// The link in the customer's e-mail carries the calculation in the URL, so it
// works on any device and in any browser — a mail opened on a phone is very
// often a different browser from the one that filled the form in, and storage
// on its own would show that visitor nothing.
//
// Only the figures travel, never name, phone, e-mail or address: a URL ends up
// in mail logs, in browser history and in a referrer header, which is no place
// for personal data. The personal fields live in localStorage only.

// The figures the visitor must fill in. They ship empty with a "f.eks."
// placeholder rather than pre-filled, so nobody sends our example numbers as
// their own — which means the submit has to gate on them. `segment` is in the
// list too: its <select> now opens on an empty option instead of preselecting
// "Privat". `installer` and the free-text notes are the optional ones.
const REQUIRED_FIELDS = ['roofArea', 'slope', 'floors', 'distance', 'segment', 'people', 'toilets', 'machines'];

const LINK_PARAM = 'b';

// Marks a page reached through calculator-return.js: the enquiry went through,
// Shopify just landed the visitor somewhere else first. It is what turns the
// receipt banner on, since Shopify's own posted_successfully? flash is spent by
// the time the visitor gets here.
const SENT_PARAM = 'sendt';
const LINK_FIELDS = [
  'roofArea',
  'slope',
  'floors',
  'distance',
  'segment',
  'people',
  'toilets',
  'machines',
  'garden',
  // A link written before the bilvask field was removed carries one extra
  // value at the end. Dropping the last entry rather than one in the middle is
  // what keeps those links parsing correctly — the surplus is ignored.
];

class TankCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.inputs = {};
    this.querySelectorAll('[data-calc-input]').forEach((el) => {
      this.inputs[el.dataset.calcInput] = el;
      el.addEventListener('input', () => this.onInput());
      el.addEventListener('change', () => this.onInput());
    });

    this.form?.addEventListener('submit', (event) => this.handleSubmit(event));

    // The print button is a plain type="button" inside the contact form, so it
    // can never submit it. `print()` is what the browser's "Save as PDF"
    // destination hangs off.
    this.querySelector('[data-calc-print]')?.addEventListener('click', () => {
      // Behind the veil there is no calculation, only the dummy result — and a
      // PDF of that under the BrugRegnvand masthead would read as a real
      // quotation. The button is unreachable behind the blur anyway; this is
      // the guard for anyone who reaches it another way.
      if (this.isGated()) return;
      this.renderPrintFields();
      window.print();
    });

    // The gate's button sends the visitor to the first thing still missing,
    // rather than submitting: the form already knows which fields are empty, and
    // a second e-mail input inside the overlay would mean two inputs for one
    // value. With everything filled it lands on the submit button instead.
    this.querySelector('[data-calc-gate-cta]')?.addEventListener('click', () => this.focusNextStep());

    this.numberFormat = new Intl.NumberFormat(this.locale());

    // Needs the number formatter, and nothing else — no inputs, no calculation,
    // no gate. Stated assumptions are not results.
    this.renderAssumptions();

    // Before recalculate(), because on the confirmation page there are no inputs
    // left to read and these saved values are the only source for the figures.
    this.restoreInputs();

    // Before the first recalculate, deliberately. A visitor arriving with a
    // calculation has already sent the form — the link only exists in the
    // e-mail they were sent, and storage is only ever written on submit — so
    // the gate has nothing left to ask for. Lifting it after recalculate would
    // leave them looking at the dummy figures, since nothing is computed while
    // the gate is up.
    if (this.hasRestoredCalculation()) this.liftVeil();

    // Reached through calculator-return.js: the enquiry is away, so the receipt
    // is shown even though Shopify's own success flash was spent on whichever
    // page it redirected to.
    if (this.wasSent()) this.revealReceipt();

    // A VISIBLE success banner is the signal that the enquiry has landed, and
    // therefore that the sign-up can go out. The banner is also rendered on the
    // form page, hidden, so the test is on visibility rather than existence.
    if (this.querySelector('[data-calc-success]:not([hidden])')) this.subscribeToNewsletter();

    this.recalculate();
    this.updateSubmitState();
  }

  locale() {
    // Shopify sets <html lang>; fall back to Danish since that is the store's
    // primary locale.
    return document.documentElement.lang || 'da-DK';
  }

  onInput() {
    this.recalculate();
    this.updateSubmitState();
  }

  getValue(name) {
    const el = this.inputs[name];
    // On the confirmation page the form has been replaced by Shopify's success
    // branch, so there is nothing to read — the figures come from what was
    // saved immediately before submitting.
    if (!el) {
      if (this.restored && Object.prototype.hasOwnProperty.call(this.restored, name)) {
        return this.restored[name];
      }
      return null;
    }
    if (el.type === 'checkbox') return el.checked;
    if (el.type === 'number') return Number(el.value || 0);
    return el.value;
  }

  nf(value) {
    return this.numberFormat.format(value);
  }

  // What the two "svarende til" figures were divided by. Without it "1.369
  // brusebade" is a number with no unit behind it, which is what the review
  // flagged.
  assumptionsText() {
    return `Omregnet med ${this.nf(SHOWER_LITERS)} liter pr. brusebad og ${this.nf(
      COFFEE_LITERS
    )} liter pr. kop kaffe.`;
  }

  // The figures the whole calculation rests on, stated rather than implied.
  // `opsamlingseffektivitet` is the two losses combined — the roof's runoff
  // coefficient and the filter's — because they are one number to the reader and
  // splitting them would invite the question of which applies first.
  //
  // Shared with buildSummary() so the sentence at the foot of the enquiry is the
  // same sentence the visitor read.
  disclaimerText() {
    const efficiency = Math.round(RUNOFF_COEFF * FILTER_LOSS_FACTOR * 100);
    return (
      `Beregningen er vejledende og bygger på ${this.nf(DKK_PER_M3)} kr. pr. m³ vand inkl. afledning, ` +
      `${this.nf(RAINFALL_MM_PER_YEAR)} mm nedbør pr. år og ${efficiency} % opsamlingseffektivitet. ` +
      'Endeligt anlæg og pris fastlægges af en specialist.'
    );
  }

  // Both sentences are already in the markup from the locale files, so a visitor
  // without JavaScript reads them too. This overwrites them from the constants
  // above, which is what keeps the stated assumptions and the arithmetic from
  // drifting apart.
  //
  // Called unconditionally, outside recalculate(): these are assumptions, not
  // results, so the gate has no reason to withhold them — and the figures they
  // explain are on the page behind the veil either way.
  renderAssumptions() {
    const note = this.querySelector('[data-calc-assumptions]');
    if (note) note.textContent = this.assumptionsText();
    this.querySelectorAll('[data-calc-disclaimer]').forEach((el) => {
      el.textContent = this.disclaimerText();
    });
  }

  computeResults() {
    const roofArea = this.getValue('roofArea') || 0;
    const slope = this.getValue('slope') || 0;
    const floors = this.getValue('floors') || 1;
    const distance = this.getValue('distance') || 0;
    const people = this.getValue('people') || 1;
    const toilets = this.getValue('toilets') || 0;
    const machines = this.getValue('machines') || 0;
    const garden = this.getValue('garden');

    // Steep roofs collect against their horizontal projection, not their
    // surface area.
    const projected = roofArea * Math.cos((Math.min(slope, 89) * Math.PI) / 180);
    const catchArea = slope > 0 ? projected : roofArea;
    const roofYield = catchArea * RAINFALL_MM_PER_YEAR * RUNOFF_COEFF * FILTER_LOSS_FACTOR;

    const flushDay = toilets > 0 ? people * TOILET_LITERS_PER_PERSON_DAY : 0;
    const washDay = machines * MACHINE_LITERS_PER_WASH;
    const rainDay = Math.round(
      flushDay + washDay + (garden ? GARDEN_LITERS_PER_DAY : 0)
    );
    const drinkDay = Math.round(people * (TOTAL_LITERS_PER_PERSON_DAY - TOILET_LITERS_PER_PERSON_DAY));

    const totalDay = rainDay + drinkDay;
    const rainShare = totalDay > 0 ? Math.round((rainDay / totalDay) * 100) : 0;
    const drinkShare = 100 - rainShare;

    const rainYear = rainDay * 365;
    const covered = Math.min(rainYear, roofYield);
    const coveragePct = rainYear > 0 ? Math.round((covered / rainYear) * 100) : 0;

    const pumpHead = Math.round(floors * 3 + distance / 10 + 2);

    this.results = {
      rainShare,
      drinkShare,
      coveragePct,
      tankSize: this.getTankSize(roofArea),
      catchArea: Math.round(catchArea),
      roofYield: Math.round(roofYield),
      system: this.getSystemRecommendation(distance, roofArea),
      pumpHead,
      pumpType: this.getPumpType(pumpHead),
      distance,
      moneySavings: Math.round((covered / 1000) * DKK_PER_M3),
      showers: Math.round(covered / SHOWER_LITERS),
      coffees: Math.round(covered / COFFEE_LITERS),
      // Day and year only. The monthly figures existed for the readout card
      // that has been removed, and nothing else — not the enquiry, not the PDF —
      // ever asked for them.
      rainDay,
      drinkDay,
      rainYear,
      drinkYear: drinkDay * 365,
    };
  }

  // True while the e-mail gate is over the results. `liftVeil` removes the
  // attribute, so this answers "has the visitor earned the figures yet".
  isGated() {
    return Boolean(this.querySelector('[data-calc-gated]'));
  }

  // Nothing is worked out while the gate is up — not into the DOM and not into
  // `this.results`. That is the whole point: with the figures never computed,
  // there is no blur to peel off in the inspector and no object to read in the
  // console. What sits behind the veil is the dummy result the section rendered
  // server-side, identical for everyone.
  //
  // The real numbers are produced in exactly two places: here, once the veil is
  // gone (the confirmation page, or a visitor arriving from their e-mail link),
  // and in `handleSubmit`, so the enquiry carries them.
  recalculate() {
    if (this.isGated()) {
      this.results = null;
      return;
    }
    this.computeResults();
    this.renderResults();
  }

  getTankSize(roofArea) {
    if (roofArea <= 200) return 4000;
    if (roofArea <= 250) return 5000;
    if (roofArea <= 500) return 10000;
    if (roofArea <= 1000) return 20000;
    if (roofArea <= 2000) return 30000;
    return 0;
  }

  getSystemRecommendation(distance, roofArea) {
    if (distance > 50) return 'Afstand over 50 m kræver beregning af rådgiver';
    if (distance <= 15) return roofArea <= 500 ? 'Sigma, Multimat eller Optima' : 'Maxima';
    if (distance < 25) return roofArea <= 500 ? 'Multimat eller Optima' : 'Maxima';
    return 'OptimaPlus (støjsvag)';
  }

  getPumpType(pumpHead) {
    if (pumpHead > 45) return 'Trykpumpe, høj løftehøjde';
    if (pumpHead > 30) return 'Trykpumpe, standard';
    return 'Standard husvandværk';
  }

  renderResults() {
    const r = this.results;

    // Headline: the share of household water that rainwater can replace.
    this.setText('rain-share', r.rainShare);
    this.setText('savings-share', r.rainShare);

    this.setSegment('bar-rain', r.rainShare, `Regnvand ${r.rainShare} %`);
    this.setSegment('bar-drink', r.drinkShare, `Drikkevand ${r.drinkShare} %`);

    const tankEl = this.querySelector('[data-result="tank-size"]');
    const tankUnit = this.querySelector('[data-result="tank-unit"]');
    if (tankEl) tankEl.textContent = r.tankSize > 0 ? this.nf(r.tankSize) : '–';
    if (tankUnit) tankUnit.hidden = r.tankSize <= 0;

    this.setText('effective-area', `${this.nf(r.catchArea)} m²`);
    this.setText('yield', `${this.nf(r.roofYield)} l/år`);
    this.setText('coverage', `${r.coveragePct} %`);
    this.setText('pump-head', `ca. ${r.pumpHead} m`);
    this.setText('pump-type', r.pumpType);
    this.setText('system', r.system);
    this.setText('distance', `${this.nf(r.distance)} m`);

    this.setText('savings-money', this.formatMoney(r.moneySavings));
    this.setText('showers', `${this.nf(r.showers)} stk/år`);
    this.setText('coffees', `${this.nf(r.coffees)} stk/år`);

    // The daily/monthly/yearly consumption split has no readout on the page any
    // more — it was an output sitting in the form column, and it slipped past
    // the e-mail gate. The values stay in `this.results` because the share, the
    // coverage and the savings are all derived from them, and because they
    // travel with the enquiry.

    this.renderPrintFields();
  }

  setSegment(key, pct, label) {
    const el = this.querySelector(`[data-result="${key}"]`);
    if (!el) return;
    el.style.width = `${Math.max(0, Math.min(100, pct))}%`;
    el.textContent = pct >= SEGMENT_LABEL_MIN_PCT ? label : '';
  }

  setText(key, value) {
    const el = this.querySelector(`[data-result="${key}"]`);
    if (el) el.textContent = value;
  }

  formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'DKK';
    try {
      return new Intl.NumberFormat(this.locale(), {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return this.nf(amount);
    }
  }

  // Where the visitor has to go next: the first required field still empty, or
  // the consent box, or — when everything is filled — the submit button itself.
  // Scrolled into view as well as focused, because on a phone the form is above
  // the veiled column and simply moving focus would leave the reader looking at
  // the overlay with no idea anything happened.
  focusNextStep() {
    const missing = this.missingFields();
    let target = missing.length > 0 ? this.inputs[missing[0]] : null;
    if (!target && !this.inputs.consent?.checked) target = this.inputs.consent;
    if (!target) target = this.querySelector('[type="submit"]');
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus after the scroll starts, not before: focusing first makes the
    // browser jump instantly and the smooth scroll then has nowhere to go.
    window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  segmentLabel() {
    const value = this.getValue('segment');
    if (value === 'business') return 'Erhverv';
    if (value === 'private') return 'Privat';
    return '—';
  }

  // Blank, not zero. `toilets`, `machines` and `distance` all take 0 as a real
  // answer, so an empty field cannot be told apart from a filled one by reading
  // the parsed number — `getValue` turns both into 0. This reads the raw value
  // instead, and falls back to the restored payload when there is no input (the
  // confirmation page).
  isBlank(name) {
    const el = this.inputs[name];
    if (el) return String(el.value ?? '').trim() === '';
    const restored = this.restored ? this.restored[name] : undefined;
    return restored === null || restored === undefined || String(restored).trim() === '';
  }

  missingFields() {
    const missing = [];
    const name = (this.getValue('name') || '').trim();
    const phone = (this.getValue('phone') || '').trim();
    const email = (this.getValue('email') || '').trim();
    const address = (this.getValue('address') || '').trim();
    if (!name) missing.push('name');
    if (!phone || phone.replace(/\D/g, '').length < 8) missing.push('phone');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) missing.push('email');
    if (!address) missing.push('address');

    // The figures now ship empty with a "f.eks." placeholder, so they have to
    // be checked here too. Without this the button would sit enabled while the
    // browser's own `required` blocked the submit — the visitor clicks and
    // nothing appears to happen.
    //
    // `checkValidity()` rather than a hand-rolled range test: it is the same
    // logic the browser will apply on submit, so the two cannot disagree about
    // `min` and `max`. Installatør is deliberately absent from this list.
    REQUIRED_FIELDS.forEach((field) => {
      if (this.isBlank(field)) {
        missing.push(field);
        return;
      }
      const el = this.inputs[field];
      if (el && typeof el.checkValidity === 'function' && !el.checkValidity()) missing.push(field);
    });

    return missing;
  }

  updateSubmitState() {
    const button = this.querySelector('[data-submit]');
    if (!button) return;
    const ready = this.missingFields().length === 0 && this.inputs.consent?.checked;
    button.disabled = !ready;
    const hint = this.querySelector('[data-submit-hint]');
    if (hint) hint.hidden = ready;
  }

  // The visitor's own inputs, as label/value pairs. One source for both the
  // enquiry email and the printed PDF, so the two can never drift apart.
  inputPairs() {
    return [
      ['Installatør', this.getValue('installer') || '—'],
      ['Installationsadresse', this.getValue('address') || '—'],
      ['Tagareal', `${this.nf(this.getValue('roofArea'))} m²`],
      ['Taghældning', `${this.getValue('slope')}°`],
      ['Antal etager', String(this.getValue('floors'))],
      ['Afstand mellem tank og teknikrum', `${this.getValue('distance')} m`],
      // Not "anything but business means private": with the field required it
      // cannot be blank at submit, but reporting an unanswered field as a
      // positive answer is how a guess reaches a specialist as fact.
      ['Anvendelse', this.segmentLabel()],
      ['Antal personer', String(this.getValue('people'))],
      ['Antal toiletter', String(this.getValue('toilets'))],
      ['Vaskemaskiner', String(this.getValue('machines'))],
      ['Vander haven', this.getValue('garden') ? 'Ja' : 'Nej'],
    ];
  }

  // The body of the e-mail BrugRegnvand receives. Shopify's contact form sends
  // `contact[body]` as PLAIN TEXT — no HTML, no styling — so the readability has
  // to come from the layout of the characters themselves: a heading line, rules
  // between sections, and labels padded to a common width so the values line up
  // in a column when read in a mail client's monospace or proportional font.
  //
  // Name, telephone and e-mail are not repeated here: they travel in Shopify's
  // own `contact[name]`, `contact[phone]` and `contact[email]` fields and appear
  // at the top of the notification as the sender.
  //
  // No ASCII rules and no space-padded columns, though both were tried. Mail
  // clients commonly render text/plain in a PROPORTIONAL font, where a row of
  // dashes comes out a ragged length and padded labels do not line up at all —
  // the alignment only ever held in a monospace preview. An uppercase heading
  // and a blank line is the plain-text convention, and it survives any client.
  emailSection(title, lines) {
    return [title.toUpperCase(), ...lines, ''];
  }

  emailPairs(pairs) {
    return pairs.map(([label, value]) => `${label}: ${value}`);
  }

  buildSummary() {
    const r = this.results;
    const stamp = new Intl.DateTimeFormat(this.locale(), {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(new Date());

    return [
      'BEREGNING FRA BRUGREGNVAND.DK',
      `Modtaget ${stamp}`,
      '',
      ...this.emailSection('Anbefaling', this.emailPairs([
        ['Anbefalet tankstørrelse', r.tankSize > 0 ? `${this.nf(r.tankSize)} liter` : '—'],
        ['Anbefalet anlæg', r.system],
        ['Anbefalet pumpe', r.pumpType],
        ['Dækket af regnvand', `${r.rainShare} % af vandforbruget`],
        ['Årlig besparelse', this.formatMoney(r.moneySavings)],
      ])),
      ...this.emailSection('Kundens oplysninger', this.emailPairs(this.inputPairs())),
      ...this.emailSection('Beregnet i detaljer', this.emailPairs([
        ['Effektivt tagareal', `${this.nf(r.catchArea)} m²`],
        ['Opsamlingspotentiale', `${this.nf(r.roofYield)} l/år`],
        ['Dækning af behov', `${r.coveragePct} %`],
        ['Anslået løftehøjde', `ca. ${r.pumpHead} m`],
      ])),
      ...this.emailSection('Forventet vandforbrug', this.emailPairs([
        ['Regnvand pr. dag', `${this.nf(r.rainDay)} liter`],
        ['Drikkevand pr. dag', `${this.nf(r.drinkDay)} liter`],
        ['Regnvand pr. år', `${this.nf(r.rainYear)} liter`],
        ['Drikkevand pr. år', `${this.nf(r.drinkYear)} liter`],
      ])),
      ...this.emailSection('Kundens bemærkninger', [this.getValue('notes') || '(ingen)']),
      // The ready-made link to the customer's own calculation. It is here
      // because a Shopify contact form only mails the shop — so whatever sends
      // the customer their copy (a form app's autoresponder, or a person) takes
      // the URL from this line rather than having to assemble it. It carries
      // the figures only, never the personal fields.
      ...this.emailSection('Link til kundens beregning', [
        this.calculationLink(),
        '(Send dette link til kunden. Det viser beregningen igen på enhver enhed.)',
      ]),
      // On the record in the enquiry itself, so there is a written trace of what
      // the visitor agreed to and when — the e-mail is timestamped above. A
      // marketing consent you cannot produce evidence of is not much use.
      ...this.emailSection('Samtykke', [
        'Vilkår for beregning og kontakt: Ja',
        `Nyhedsbrev: ${this.inputs.newsletter ? (this.inputs.newsletter.checked ? 'Ja' : 'Nej') : 'ikke tilbudt'}`,
        // Recorded here because the sign-up itself now happens on the next page:
        // if it never goes out, this line is the evidence that it was asked for.

      ]),
      this.disclaimerText(),
    ].join('\n');
  }

  // Print-only content: the date and the visitor's inputs. Both are written
  // from JS rather than Liquid — Shopify's full-page cache would freeze a
  // Liquid `'now' | date` at whenever the page was last rendered for somebody
  // else, and the inputs only exist in the browser anyway.
  renderPrintFields() {
    const dateEl = this.querySelector('[data-print-date]');
    if (dateEl) {
      dateEl.textContent = new Intl.DateTimeFormat(this.locale(), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date());
    }

    const list = this.querySelector('[data-print-inputs]');
    if (!list) return;
    // Rebuilt wholesale on every keystroke: the rows are a pure function of the
    // form, so diffing them would be more code for no gain.
    list.replaceChildren(
      ...this.inputPairs().flatMap(([label, value]) => {
        const row = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = label;
        dd.textContent = value;
        row.append(dt, dd);
        return [row];
      })
    );
  }

  handleSubmit(event) {
    if (this.missingFields().length > 0 || !this.inputs.consent?.checked) {
      event.preventDefault();
      this.updateSubmitState();
      return;
    }

    // Worked out here rather than live, because nothing is computed while the
    // gate is up — see recalculate(). This is the moment the visitor has earned
    // the figures, and the enquiry has to carry them.
    this.computeResults();

    const bodyField = this.querySelector('[data-calc-body]');
    if (bodyField) bodyField.value = this.buildSummary();

    // The event is left alone from here on: the browser posts the form itself.
    // See the note at the top of this file for why nothing is intercepted.
    this.saveInputs();
    this.setSending(true);
  }

  // Signs the visitor up, on the confirmation page, if they ticked the box
  // before submitting. Deliberately NOT at submit time: a beacon fired there
  // posted to /contact in the same instant the browser was posting the enquiry
  // to /contact, and two posts to one endpoint from one browser at once is both
  // a race and the shape spam protection looks for. Here the enquiry has already
  // landed and nothing else is in flight, so it can be a plain fetch whose
  // result is actually knowable.
  //
  // Guarded against running twice: a reload of the confirmation page, or a
  // visitor pressing back and forward, must not post the sign-up again.
  async subscribeToNewsletter() {
    if (!this.restored || this.restored.newsletter !== true) return;
    if (this.restored.newsletterSent === true) return;
    const email = this.restored.email;
    if (!email) return;

    // Written before the request, not after: a sign-up that fails is a smaller
    // loss than one that posts on every reload of this page. The consent is on
    // the record in the enquiry body regardless.
    this.markNewsletterSent();

    // Shopify's own customer form: the same fields {% form 'customer' %} posts.
    const data = new FormData();
    data.append('form_type', 'customer');
    data.append('utf8', '✓');
    data.append('contact[tags]', this.dataset.newsletterTag || 'newsletter');
    data.append('contact[email]', email);
    if (this.restored.name) data.append('contact[first_name]', this.restored.name);

    try {
      await fetch(this.dataset.newsletterUrl || '/contact', {
        method: 'POST',
        body: data,
        // Shopify's own form posts carry the session cookie, and so must this.
        credentials: 'same-origin',
      });
    } catch (error) {
      // Swallowed on purpose. The visitor is looking at their finished
      // calculation; an error about a newsletter here would read as though the
      // enquiry had gone wrong. The consent is in the enquiry body either way.
    }
  }

  markNewsletterSent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.values) return;
      parsed.values.newsletterSent = true;
      this.restored.newsletterSent = true;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch (error) {
      // Without storage the guard is gone, so a reload could post twice.
      // Shopify treats a repeat sign-up for the same address as the same
      // customer, so the cost is a duplicate request, not a duplicate record.
    }
  }

  // Saved with the input names the calculator already uses as keys, so
  // restoring is a straight lookup. The expiry travels in the payload rather
  // than being left to the storage backend, because localStorage has none.
  saveInputs() {
    try {
      const values = {};
      Object.keys(this.inputs).forEach((name) => {
        values[name] = this.getValue(name);
      });

      // Where to send the visitor if Shopify loses them. Its captcha challenge
      // drops them on the shop's root after they solve it, and
      // calculator-return.js reads this to finish the journey. Stored finished
      // rather than rebuilt over there: that file runs on the front page and
      // loads none of this one, and a relative import between two theme assets
      // loses the fingerprint asset_url appends and is then cached for a year.
      const link = new URL(this.calculationLink());
      link.searchParams.set(SENT_PARAM, '1');
      values.returnUrl = link.pathname + link.search;

      localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), values }));
    } catch (error) {
      // Private mode, or storage full. The confirmation page then shows
      // placeholders rather than the figures — no worse than before the feature
      // existed, and the link in the e-mail still carries them.
    }
  }

  // Two sources, and the URL wins: a visitor following the link from their
  // e-mail is asking for THAT calculation, which may not be the last one this
  // browser made. Storage fills in the personal fields the link deliberately
  // does not carry.
  restoreInputs() {
    this.restored = null;

    const stored = this.readStored();
    if (stored) this.restored = { ...stored };

    const fromLink = this.readLink();
    if (fromLink) this.restored = { ...(this.restored || {}), ...fromLink };

    // With the form on the page, `getValue` reads the empty inputs rather than
    // this object — so the values have to be written into the fields, which
    // also lets the visitor adjust one and recalculate.
    if (this.restored) this.applyRestored();
  }

  readStored() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);

      // An older payload was a bare value map with no timestamp. Treat it as
      // expired rather than trusting it for an unknown length of time.
      if (!parsed || typeof parsed.savedAt !== 'number' || !parsed.values) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      if (Date.now() - parsed.savedAt > STORAGE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed.values;
    } catch (error) {
      return null;
    }
  }

  // `?b=180.25.2.12.private.4.2.1.1.0` — positional, in LINK_FIELDS order, so
  // the link stays short enough to survive a mail client that wraps long URLs.
  // A malformed or truncated value yields whatever prefix did parse; a link
  // that carries nothing usable returns null and the page behaves as normal.
  readLink() {
    try {
      const raw = new URLSearchParams(window.location.search).get(LINK_PARAM);
      if (!raw) return null;
      const parts = raw.split('.');
      const out = {};
      LINK_FIELDS.forEach((name, i) => {
        const part = parts[i];
        if (part === undefined || part === '') return;
        if (name === 'segment') {
          if (part === 'private' || part === 'business') out[name] = part;
          return;
        }
        if (name === 'garden') {
          out[name] = part === '1';
          return;
        }
        const n = Number(part);
        if (Number.isFinite(n) && n >= 0) out[name] = n;
      });
      return Object.keys(out).length > 0 ? out : null;
    } catch (error) {
      return null;
    }
  }

  applyRestored() {
    Object.entries(this.restored).forEach(([name, value]) => {
      const el = this.inputs[name];
      if (!el || value === null || value === undefined) return;
      // `segment` is a <select>, so plain assignment covers it alongside the
      // number and text fields; only a checkbox needs the other property.
      if (el.type === 'checkbox') {
        el.checked = Boolean(value);
      } else {
        el.value = value;
      }
    });
  }

  // The link that goes in the customer's e-mail. Absolute, because it has to
  // work from a mail client; built from the page's own origin and path so it
  // follows the page wherever the merchant moves the calculator.
  calculationLink() {
    const compact = LINK_FIELDS.map((name) => {
      const value = this.getValue(name);
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'boolean') return value ? '1' : '0';
      return String(value);
    }).join('.');
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set(LINK_PARAM, compact);
    return url.toString();
  }

  // True when the visitor arrived on the form page carrying a calculation —
  // from the e-mail link, or from storage inside the seven days. The figures are
  // already rendered; what has to go is the veil that asks for an e-mail the
  // visitor has plainly already given.
  hasRestoredCalculation() {
    return Boolean(this.restored && (this.getValue('roofArea') || 0) > 0);
  }

  wasSent() {
    try {
      return new URLSearchParams(window.location.search).get(SENT_PARAM) === '1';
    } catch (error) {
      return false;
    }
  }

  revealReceipt() {
    const banner = this.querySelector('[data-calc-success]');
    if (banner) banner.hidden = false;
    // The figures were this visitor's before they were sent, so there is
    // nothing left for the gate to ask for.
    this.liftVeil();
  }

  liftVeil() {
    const results = this.querySelector('[data-calc-gated]');
    if (results) {
      results.removeAttribute('data-calc-gated');
      results.classList.remove('tank-calculator__results--gated');
    }
    this.querySelector('.tank-calculator__gate')?.remove();
  }

  setSending(sending) {
    const button = this.querySelector('[data-submit]');
    if (!button) return;
    if (sending) {
      this.submitLabel = button.textContent;
      button.disabled = true;
      button.textContent = this.dataset.sendingLabel || 'Sender…';
    } else {
      button.disabled = false;
      if (this.submitLabel) button.textContent = this.submitLabel;
    }
  }

}

if (!customElements.get('tank-calculator')) {
  customElements.define('tank-calculator', TankCalculator);
}
